import React, { useCallback, useEffect } from "react";
import Layout from "../../../Components/Layout";
import ProGrid from "../../../Components/ProGrid/v2";
import useColumn from "../../../../utils/customHooks/useColumns";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import { useNavigate } from "react-router-dom";
import usePageState from "../../../../utils/customHooks/usePageState";
import useDebouncedFilter from "../../../../utils/customHooks/useDebouncedFilter";
import { STAUTES } from "../../../../utils/constant";
import { ADMIN_SPONSOR_INFO } from "../../../../utils/services/apiPath";
import { deleteApi, getApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { createODataQuery, convertMuiFilterToOData } from "../../../../utils/services/oData";
import { apprenticeSponsorInfoPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";

const getRedirectUrl = params => {
  return `/settings/sponsor-info/${params.id}`;
};

export default function SponsorList() {
  const {
    currentPage,
    pageSize,
    status,
    data: sponsorInfoListData,
    rowCount,
    sortModel,
    searchString,
    filterModel,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString,
    setFilterModel
  } = usePageState();
  const navigate = useNavigate();
  const onFilterChange = useDebouncedFilter(setFilterModel);
  const { "Sponsor List": sponsorListPermission } = usePermission(apprenticeSponsorInfoPermissionSelector, "Sponsor List");
  const { canView, canAdd, canExport, canDelete } = sponsorListPermission || {};
  const columns = useColumn(
    [
      {
        field: "companyName",
        headerName: "Company Name",
        renderCell: params => <NameCell params={params} getRedirectUrl={getRedirectUrl} />
      },
      {
        field: "contactPhoneNumber",
        headerName: "Contact"
      },
      {
        field: "email",
        headerName: "Email"
      },
      {
        field: "sponsorInformationId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} disabled={params?.row?.readOnly} />
      }
    ],
    canDelete
  );

  const getSponsorInfoList = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ["companyName", "contactPhoneNumber", "email"];
    
    // Convert MUI filter model to OData filter
    const gridFilterQuery = convertMuiFilterToOData(filterModel);
    
    // Combine queries including grid filter
    const allQueries = [gridFilterQuery].filter(q => q && q.length > 0);
    
    const { data, totalRecords, error } = await getApi(
      ADMIN_SPONSOR_INFO + createODataQuery(searchableFields, null, allQueries, searchString, sortModel, pageSize, currentPage)
    );
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, filterModel, setPageState, setStatus]);

  const onDelete = async id => {
    const { error } = await deleteApi(`${ADMIN_SPONSOR_INFO}/${id}`);
    if (error) return errorToast(error);
    successToast("Sponsor's Info deleted successfully");
    getSponsorInfoList();
  };

  useEffect(() => {
    getSponsorInfoList();
  }, [getSponsorInfoList]);

  const addButtonAction = () => {
    navigate(`/settings/sponsor-info/add`);
  };

  return (
    <Layout canView={canView}>
      <ProGrid
        loading={status === STAUTES.LOADING}
        title="Sponsor Information"
        columns={columns}
        rows={sponsorInfoListData}
        addButtonLabel="+ Add Sponsor Info"
        addButtonAction={addButtonAction}
        searchMode={!!searchString}
        searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        hideAddButton={!canAdd}
        hideExport={!canExport}
        options={{
          getRowId: row => row.sponsorInformationId,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          filteringMode: "server",
          onSortModelChange: setSortModel,
          onFilterModelChange: onFilterChange
        }}
      />
    </Layout>
  );
}
